import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Text } from '@rsos/components/capstone/base';
import {
  setIsEditing,
  useActivateTransportMutation,
  parseTransportVerbatimMessage,
  type EtlTransportResponse,
  ComputedStatusItem,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { getEtlSubmissionErrorMessage } from '@rsos/reduxDataStore/src/utils/errorUtils';
import { trackEditAirAmbulanceRequest } from '../../../gainsightTrackings/airAmbulanceTrackings';
import { Loader } from '../AirAmbulanceForm/AirAmbulanceFormStandalone.styles';
import { LAUNCH_TYPES } from '../AirAmbulanceForm/constants';
import AirAmbulanceEtlCancelModal from './AirAmbulanceEtlCancelModal';

/**
 * Returns true when the most recent edit-location status is still pending
 * (i.e. not yet completed or disregarded by GMR).
 */
function hasUnresolvedEditLocation(statuses: ComputedStatusItem[]): boolean {
  for (let i = statuses.length - 1; i >= 0; i--) {
    if (statuses[i].type === 'editLocationPending') return true;
    if (
      statuses[i].type === 'editLocationCompleted' ||
      statuses[i].type === 'editLocationDisregarded'
    )
      return false;
  }
  return false;
}

/**
 * Returns true when the most recent cancellation request is still pending
 * (i.e. not yet completed or disregarded by GMR).
 */
function hasUnresolvedCancel(statuses: ComputedStatusItem[]): boolean {
  for (let i = statuses.length - 1; i >= 0; i--) {
    if (statuses[i].type === 'cancelPending') return true;
    if (
      statuses[i].type === 'cancelled' ||
      statuses[i].type === 'cancelCompleted' ||
      statuses[i].type === 'cancelDisregarded'
    )
      return false;
  }
  return false;
}

interface AirAmbulanceEtlStatusControlsProps {
  transport: EtlTransportResponse;
}

/**
 * Renders the set of action buttons on the bottom of the status page.
 */
function AirAmbulanceEtlStatusControls({
  transport,
}: AirAmbulanceEtlStatusControlsProps): JSX.Element {
  // Extract computed fields from the transport object
  const { source, editEnabled, cancelEnabled, activateEnabled } = {
    ...transport.computed_fields,
  };

  // Calculate if the order originated outside of UNITE. Orders initiated
  // from outside UNITE cannot be edited, cancelled, or activated
  const isExternalOrder = source.toLowerCase() !== 'unite';

  // Calculate if the order is a standby order. We should only show the
  // activate button if the order is a standby order
  const verbatim = parseTransportVerbatimMessage(transport.verbatim_message);
  const tripLeg = verbatim?.tripLeg?.[0];
  const isStandby = Boolean(tripLeg?.airDetails?.idenStandBy);

  // Check if the request is already in a cancelled and set the tool tip
  const isAlreadyCancelled =
    transport.computed_fields.statuses.at(-1)?.type === 'cancelled';
  const isCancelPending = hasUnresolvedCancel(
    transport.computed_fields.statuses,
  );
  const cancelledTitle = isAlreadyCancelled
    ? 'This request has been cancelled'
    : isCancelPending
      ? 'Cancellation pending'
      : undefined;

  // Check if an edit is pending and set the tool tip
  const isEditPending = hasUnresolvedEditLocation(
    transport.computed_fields.statuses,
  );
  const editDisabledTitle =
    cancelledTitle ?? (isEditPending ? 'Location edit pending' : undefined);

  // Used to dispatch action to activate a transport
  const dispatch = useDispatch();
  const [activateTransport] = useActivateTransportMutation();

  // Control the state of the activate button and error message box
  const [isActivating, setIsActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);

  // Control the state of the cancel model
  const [showCancelModal, setShowCancelModal] = useState(false);

  // GainSight tracking for editing request.  Also dispatches action to
  // update the state of resource request panel to editing the transport.
  const handleEditRequest = () => {
    trackEditAirAmbulanceRequest({ requestId: transport.gmr_transport_id });
    dispatch(setIsEditing(true));
  };

  // Make API call to activate transport and update error message if any.
  const handleActivateStandby = async () => {
    setIsActivating(true);
    // Clear any error from a previous activation attempt.
    setActivateError(null);
    try {
      await activateTransport({ transportId: transport.id }).unwrap();
    } catch (error) {
      setActivateError(getEtlSubmissionErrorMessage(error));

      // eslint-disable-next-line no-console -- track errors in sentry
      console.error('Activate standby error:', error);
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <>
      <Box mx="8px" mb={2} pt={4}>
        {isExternalOrder ? (
          <Text size="small" color="secondaryText">
            External orders cannot be edited.
          </Text>
        ) : (
          <>
            {activateError && (
              <Box
                p={3}
                mb={1}
                bg="lightDanger"
                borderRadius="4px"
                role="alert"
              >
                <Text color="background" size="small">
                  {activateError}
                </Text>
              </Box>
            )}
            {isStandby && (
              <Box mb={1}>
                <Button
                  grow
                  btnType="primary"
                  type="button"
                  mx={0}
                  onClick={handleActivateStandby}
                  disabled={!activateEnabled}
                  title={cancelledTitle}
                >
                  {isActivating ? (
                    <Loader />
                  ) : (
                    `Activate ${LAUNCH_TYPES.STANDBY}`
                  )}
                </Button>
              </Box>
            )}

            <Box mb={1}>
              <Button
                grow
                btnType="tertiary"
                type="button"
                mx={0}
                onClick={handleEditRequest}
                disabled={!editEnabled}
                title={editDisabledTitle}
              >
                Edit Request
              </Button>
            </Box>

            <Box mb={1}>
              <Button
                grow
                btnType="warning_tertiary"
                type="button"
                mx={0}
                onClick={() => setShowCancelModal(true)}
                disabled={!cancelEnabled}
                title={cancelledTitle}
              >
                Cancel Request
              </Button>
            </Box>
          </>
        )}
      </Box>

      <AirAmbulanceEtlCancelModal
        transport={transport}
        showModal={showCancelModal}
        onClose={() => setShowCancelModal(false)}
      />
    </>
  );
}

export default AirAmbulanceEtlStatusControls;
